/**
 * The between-missions popup: what the sortie just flown did to the career,
 * one page at a time.
 *
 * Split out of `career-surface.ts` when B5's comments pushed that file over the
 * size gate, and it is the right seam anyway — this is pure. `career-view.ts`
 * decides what the *sections* are (claims, citation, promotion, board of
 * inquiry, casualty report, the ending); this only paginates them into the
 * original's own popup widget. Nothing here reaches the store, so the whole
 * death report can be walked in a test without one.
 *
 * The caller owns the page number and the cursor. That is deliberate and it is
 * what B5 was: this dialog is *derived*, rebuilt on every paint, so it must
 * never be written back into the model as if it were state — see
 * `career-surface.ts`'s `setDialog`.
 */

import type { CareerConsequence } from "@red-opus/engine";
import { consequenceHeadline, consequenceSections } from "../../career/career-view.ts";
import type { ShellDialog } from "./shell-screen.ts";

/** How many lines of a section the popup will show before the rest is dropped. */
const MAX_BODY_LINES = 6;

/**
 * Page `index` of `consequence`'s report, or `undefined` once the pages run
 * out — which is how the caller knows the player has read all of it.
 *
 * The last page's button is `acknowledge` and every earlier one's is
 * `next-consequence`, so a report of any length ends by clearing itself.
 */
export function consequenceDialog(consequence: CareerConsequence, index: number): ShellDialog | undefined {
  const sections = consequenceSections(consequence);
  const section = sections[index];
  // A ceremony section is a full screen in the original (citation, promotion,
  // Board of Inquiry — `career-surface.ts`'s `ceremony()`), not a popup, so it
  // yields no dialog and the screen underneath is the page.
  if (!section || section.ceremony) return undefined;
  const last = index >= sections.length - 1;
  // The date, the sortie and its score head the report, once.
  const body = index === 0 ? [consequenceHeadline(consequence), ...section.lines] : section.lines;
  return {
    title: section.heading,
    body: body.slice(0, MAX_BODY_LINES),
    rows: [
      {
        id: "continue",
        label: "CONTINUE",
        emblem: "none",
        action: { kind: "command", command: last ? "acknowledge" : "next-consequence" },
      },
    ],
    focus: 0,
  };
}
